"use client";

import { useCatalogFilters, type CategorySlug } from "@/stores/catalog-filters";
import { hasAnyFilter } from "@/stores/catalog-filters";
import type { PlainCatalogStrings } from "@/lib/i18n/catalog-strings";

const CATEGORIES: CategorySlug[] = [
  "rose",
  "garden-spray-rose",
  "hydrangea",
  "specialty",
  "filler-green",
];

const PILL_BASE =
  "whitespace-nowrap rounded-md border px-3.5 py-2.5 text-[12px] font-semibold transition-all duration-150 cursor-pointer";
const PILL_DEFAULT =
  "border-brand-border bg-white text-brand-text-muted hover:border-brand-teal hover:bg-brand-teal-soft hover:text-brand-teal-dark";
const PILL_ACTIVE =
  "border-brand-text bg-brand-text text-white hover:border-brand-teal-dark hover:bg-brand-teal-dark";

export function CategoryTabs({
  strings,
}: {
  strings: PlainCatalogStrings;
}) {
  const categories = useCatalogFilters((s) => s.categories);
  const colors = useCatalogFilters((s) => s.colors);
  const lengths = useCatalogFilters((s) => s.lengths);
  const price = useCatalogFilters((s) => s.price);
  const sort = useCatalogFilters((s) => s.sort);
  const toggleCategory = useCatalogFilters((s) => s.toggleCategory);
  const clearAll = useCatalogFilters((s) => s.clearAll);

  const allActive = categories.length === 0;
  const anyFilter = hasAnyFilter({
    categories,
    colors,
    lengths,
    price,
    sort,
  });

  return (
    <nav
      aria-label="Category tabs"
      className="flex flex-wrap items-center gap-2 border-b border-brand-border pb-2"
    >
      <button
        type="button"
        data-testid="category-tab-all"
        aria-pressed={allActive}
        onClick={() => {
          if (categories.length === 0) return;
          // Only clear categories, leave other filter dimensions intact.
          // (That matches how Tesla's "All" inside one dimension works -
          // it scopes to that dimension. Cross-dimension reset is the
          // separate "Clear all" link below.)
          categories.forEach((c) => toggleCategory(c));
        }}
        className={`${PILL_BASE} ${allActive ? PILL_ACTIVE : PILL_DEFAULT}`}
      >
        {strings.categoryAll}
      </button>
      {CATEGORIES.map((cat) => {
        const isActive = categories.includes(cat);
        return (
          <button
            key={cat}
            type="button"
            data-testid={`category-tab-${cat}`}
            aria-pressed={isActive}
            onClick={() => toggleCategory(cat)}
            className={`${PILL_BASE} ${isActive ? PILL_ACTIVE : PILL_DEFAULT}`}
          >
            {strings.category[cat]}
          </button>
        );
      })}
      {anyFilter && (
        <button
          type="button"
          data-testid="category-tabs-clear-all"
          onClick={clearAll}
          className="ml-auto cursor-pointer text-[12px] font-semibold text-brand-teal-dark hover:underline"
        >
          Clear all
        </button>
      )}
    </nav>
  );
}
